import { cx } from 'class-variance-authority';
import { useFormatter, useTranslations } from 'next-intl';
import { PropsWithChildren, ReactNode } from 'react';

import { useCobranded } from '@/app/contexts/CobrandedProvider';
import { PartnerConfig } from '@/types';

import AddressDisplay, { type AddressDisplayProps } from '../AddressDisplay';
import SectionHeading from '../SectionHeading';
import Svg from '../Svg';
import { SvgName } from '../Svg/types';

export interface OrderInfoProps {
  readonly className?: string;
  readonly address?: AddressDisplayProps['address'];
  readonly email?: string;
  readonly cobrandedConfig?: PartnerConfig;
  readonly details?: {
    readonly number: string;
    readonly date: Date;
    readonly quantity: number;
  };
}

export const Info = ({
  heading,
  children,
}: PropsWithChildren<{ readonly heading?: ReactNode }>): JSX.Element => {
  return heading ? (
    <section
      data-testid="order-info-section"
      className="flex w-48 min-w-min shrink-0 grow flex-col md:w-auto"
    >
      <SectionHeading Element="h3" weight="bold" variant="h6">
        {heading}
      </SectionHeading>
      {children}
    </section>
  ) : (
    <div className="flex w-48 min-w-min shrink-0 grow flex-col md:w-auto">
      {children}
    </div>
  );
};

const OrderInfo = ({
  address,
  email,
  details,
  className,
  cobrandedConfig,
}: OrderInfoProps): JSX.Element | null => {
  const t = useTranslations();
  const format = useFormatter();
  const isCobranded = cobrandedConfig?.logo;
  const { logoName } = useCobranded();

  if (
    !address &&
    !email &&
    !details?.number &&
    !details?.date &&
    !details?.quantity
  ) {
    return null;
  }

  return (
    <div
      data-testid="order-info"
      className={cx('flex gap-4 flex-wrap justify-between', className)}
    >
      {isCobranded && !logoName && (
        <Info>
          <Svg
            className="h-[40px] w-[74px]"
            name={cobrandedConfig.logo as SvgName}
            data-testid={`${cobrandedConfig.displayName}-svg`}
          />
        </Info>
      )}

      {details?.number && details?.date && details?.quantity ? (
        <Info heading={t('eop_order_number_text', { number: details.number })}>
          <ol>
            <li>
              {format.dateTime(details.date, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </li>

            <li>
              {t('eop_quantity_text', {
                number: details.quantity,
              })}
            </li>
          </ol>
        </Info>
      ) : null}

      {email && (
        <Info heading={t('eop_order_email')}>
          <p>{email}</p>
        </Info>
      )}

      {address && (
        <Info heading={t('eop_order_shippingaddress')}>
          <AddressDisplay address={address} />
        </Info>
      )}
    </div>
  );
};

export default OrderInfo;
